import {createApi} from "@reduxjs/toolkit/query/react"
import baseQuery from "../../utils/apiIIKOConfig"
import {IBasicResponse} from "../../types/api"
import {
    IOrderAssignCourierParams,
    IOrderByIdParams,
    IOrderCancellationParams,
    IOrderCloseParams,
    IDeliveryCenterOrdersParams,
    IOrderDeliveredStrictlyParams,
    IOrderDetails,
    IOrderDictionary,
    IOrderAuditLogItem,
    IOrder,
    IOrdersListParams,
    IOrdersListResponse
} from "./OrderApiTypes"
import {getDeliveryCenterPageDecision} from "./orderPagination"

const DELIVERY_CENTER_PAGE_SIZE = 100

export const orderApi = createApi({
    reducerPath: "orderApi",
    baseQuery,
    tagTypes: ["orders"],
    endpoints: (build) => ({
        getOrders: build.query<IBasicResponse<IOrdersListResponse>, IOrdersListParams>({
            query: (body) => ({
                body,
                url: "v1/orders/list",
                method: "POST",
            }),
            providesTags: ["orders"]
        }),
        getDeliveryCenterOrders: build.query<IBasicResponse<IOrdersListResponse>, IDeliveryCenterOrdersParams>({
            queryFn: async ({branch_id_list, status_list, date_from, date_to}, _queryApi, _extraOptions, fetchWithBQ) => {
                const orders = new Map<number, IOrder>()
                let page = 1
                let totalElements = 0
                let totalPages = 1
                let responseMeta: Omit<IBasicResponse<IOrdersListResponse>, "data"> = {
                    success: true
                }

                do {
                    const response = await fetchWithBQ({
                        url: "v1/orders/list",
                        method: "POST",
                        body: {
                            page,
                            size: DELIVERY_CENTER_PAGE_SIZE,
                            type: "DELIVERY",
                            status_list,
                            branch_id_list,
                            date_from,
                            date_to
                        }
                    })

                    if (response.error) {
                        return {error: response.error}
                    }

                    const responseData = response.data as IBasicResponse<IOrdersListResponse>
                    const pageData = responseData.data
                    const content = pageData?.content || []
                    const previousOrderCount = orders.size

                    responseMeta = {
                        success: responseData.success,
                        message: responseData.message
                    }
                    totalElements = pageData?.total_elements || orders.size + content.length
                    totalPages = pageData?.total_pages || Math.max(1, Math.ceil(totalElements / DELIVERY_CENTER_PAGE_SIZE))
                    content.forEach((order) => orders.set(order.id, order))

                    const pageDecision = getDeliveryCenterPageDecision({
                        page,
                        totalPages,
                        pageSize: DELIVERY_CENTER_PAGE_SIZE,
                        receivedCount: content.length,
                        previousOrderCount,
                        currentOrderCount: orders.size,
                        totalElements,
                        success: responseData.success
                    })

                    if (pageDecision.error) {
                        return {
                            error: {
                                status: "CUSTOM_ERROR",
                                error: pageDecision.error
                            }
                        }
                    }

                    if (!pageDecision.shouldContinue) {
                        break
                    }

                    page += 1
                } while (page <= totalPages && orders.size < totalElements)

                return {
                    data: {
                        ...responseMeta,
                        data: {
                            content: Array.from(orders.values()),
                            page: 1,
                            size: orders.size,
                            total_elements: orders.size
                        }
                    }
                }
            }
        }),
        getOrderById: build.query<IBasicResponse<IOrderDetails>, IOrderByIdParams>({
            query: ({id}) => ({
                url: `v1/orders/${id}/detailed`,
                method: "GET",
            }),
            providesTags: ["orders"]
        }),
        getOrderAuditLogList: build.query<IBasicResponse<IOrderAuditLogItem[]>, IOrderByIdParams>({
            query: ({id}) => ({
                url: `v1/orders/${id}/audit-logs`,
                method: "GET",
            }),
            providesTags: ["orders"]
        }),
        assignOrderCourier: build.mutation<IBasicResponse<void>, IOrderAssignCourierParams>({
            query: ({id, courierId}) => ({
                url: `v1/orders/${id}/couriers/${courierId}`,
                method: "PATCH",
            }),
            invalidatesTags: ["orders"]
        }),
        cancelOrder: build.mutation<IBasicResponse<void>, IOrderCancellationParams>({
            query: ({id, comment}) => ({
                url: `v1/orders/${id}/cancellation`,
                method: "PATCH",
                body: {comment}
            }),
            invalidatesTags: ["orders"]
        }),
        closeOrder: build.mutation<IBasicResponse<void>, IOrderCloseParams>({
            query: ({id}) => ({
                url: `v1/orders/${id}/closing`,
                method: "PATCH",
            }),
            invalidatesTags: ["orders"]
        }),
        recreateOrder: build.mutation<IBasicResponse<void>, IOrderByIdParams>({
            query: ({id}) => ({
                url: `v1/orders/${id}/recreation`,
                method: "POST",
            }),
            invalidatesTags: ["orders"]
        }),
        deliverOrderStrictly: build.mutation<IBasicResponse<void>, IOrderDeliveredStrictlyParams>({
            query: ({id, comment}) => ({
                url: `v1/orders/${id}/delivered/strictly`,
                method: "PATCH",
                body: {comment}
            }),
            invalidatesTags: ["orders"]
        }),
        getOrderSources: build.query<IBasicResponse<IOrderDictionary[]>, void>({
            query: () => ({
                url: "v1/orders/sources/list",
                method: "GET",
            }),
            providesTags: ["orders"]
        })
    })
})

export const {
    useGetOrdersQuery,
    useGetOrderSourcesQuery,
    useLazyGetDeliveryCenterOrdersQuery,
    useLazyGetOrderByIdQuery,
    useGetOrderByIdQuery,
    useGetOrderAuditLogListQuery,
    useAssignOrderCourierMutation,
    useCancelOrderMutation,
    useCloseOrderMutation,
    useRecreateOrderMutation,
    useDeliverOrderStrictlyMutation
} = orderApi
